import type { ComponentProps } from 'react';
import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { useState } from 'react';
import { Collapse } from 'react-collapse';
import { usePageContext } from 'Context/PageContext';
import { OFFER_PUSH, GA_DEFAULT_PAYLOAD } from 'Tracking/constants';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { truncate } from 'Components/utils/string/string.util';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { Countdown } from 'Components/frameworks/countdown/Countdown';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import Image from 'Components/frameworks/image/Image';
import type { RatioDisplay } from 'Components/frameworks/image/Image';
import { themes } from './OfferPush.themes';
type Props = CMSOffersPushColumn & {
  id: number;
  ratioDisplay?: RatioDisplay;
};
export const OfferPush = ({
  countdown,
  id,
  link,
  mediaUrl,
  subTitle,
  tag,
  theming: { titleColor, subtitleColor },
  title,
  ratioDisplay = 1 / 3,
}: Props) => {
  const [isHover, setHover] = useState(false);
  const pageContext = usePageContext();
  const siteSection = pageContext?.siteSection || '';
  const { thRoot, thImage, thHeader, thHeaderItem, thTitle, thFooter, thSubTitle } = themes.default(
    {
      titleColor,
      subtitleColor,
    },
  );
  const track = useTracking({
    ...GA_DEFAULT_PAYLOAD,
    actionGA: OFFER_PUSH.ACTION,
    labelGA: title,
    thumbnailList: `${siteSection}_offers`,
    thumbnailPosition: id,
  });
  const shouldDisplayOffer = !countdown.dateTimeEnd && tag[1]?.text;
  const shouldDisplayCountdown = countdown.dateTimeEnd;
  const { isRTL } = useResponsive();
  return (
    <article className={thRoot} data-testid="offer-push-container" dir={isRTL ? 'rtl' : 'ltr'}>
      <DynamicLink
        {...asWidgetProps<ComponentProps<typeof DynamicLink>>({
          className: 'relative block h-full overflow-hidden',
          'data-testid': `offer-push-${id}-link`,
          onBlur: () => setHover(false),
          onClick: () => track(),
          onFocus: () => setHover(true),
          onMouseEnter: () => setHover(true),
          onMouseLeave: () => setHover(false),
          url: link,
        })}
      >
        <div className={thImage}>
          <Image
            {...asWidgetProps<ComponentProps<typeof Image>>({
              altMsg: title,
              groupKey: 'offers-push',
              hasLazyLoading: true,
              imageUrl: mediaUrl,
              ratioKey: ratioUtils.XL,
              ratioDisplay,
            })}
          />
        </div>
        <div className={thHeader}>
          {tag[0]?.text && (
            <p className={thHeaderItem(tag[0].theming)} data-testid={`offer-push-${id}-tag-first`}>
              <span className="block truncate pt-1">{tag[0].text}</span>
            </p>
          )}
          {shouldDisplayCountdown && (
            <Countdown
              bgColor={countdown.backgroundColor}
              className="mx-5 leading-1"
              color={countdown.textColor}
              dataTestId={`offer-push-${id}-countdown`}
              dateTimeEnd={countdown.dateTimeEnd}
              days={countdown.days}
              hours={countdown.hours}
              minutes={countdown.minutes}
              seconds={countdown.seconds}
              theme="offerPush"
            />
          )}
          {shouldDisplayOffer && (
            <p className={thHeaderItem(tag[1].theming)} data-testid={`offer-push-${id}-tag-second`}>
              <span className="block truncate pt-1">{tag[1].text}</span>
            </p>
          )}
        </div>

        <div className={thFooter}>
          {title && (
            <h3 className={thTitle} data-testid={`offer-push-${id}-title`}>
              {title}
            </h3>
          )}
          {subTitle && (
            <Collapse isOpened={isHover}>
              <p className={thSubTitle} data-testid={`offer-push-${id}-subtitle`}>
                {truncate(subTitle, 115)}
              </p>
            </Collapse>
          )}
        </div>
      </DynamicLink>
    </article>
  );
};
